import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/auth';
import Layout from '../components/Layout';

const HospitalManagement = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const API_URL = 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/hospitals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch hospitals');
      }

      const hospitalsData = await response.json();
      setHospitals(hospitalsData);
    } catch (err) {
      setError('Failed to load hospitals');
      console.error('Hospitals fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = getToken();
      const url = editingHospital 
        ? `${API_URL}/hospitals/${editingHospital.id}`
        : `${API_URL}/hospitals`;
      
      const method = editingHospital ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save hospital');
      }

      // Refresh hospitals list
      await fetchHospitals();
      
      // Reset form
      setFormData({ name: '', description: '' });
      setShowAddForm(false);
      setEditingHospital(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (hospital) => {
    setEditingHospital(hospital);
    setFormData({ 
      name: hospital.name, 
      description: hospital.description || '' 
    });
    setShowAddForm(true);
  };

  const handleDelete = async (hospital) => {
    if (!window.confirm(`Are you sure you want to delete "${hospital.name}"?`)) {
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/hospitals/${hospital.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete hospital');
      }

      // Refresh hospitals list
      await fetchHospitals();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', description: '' });
    setShowAddForm(false);
    setEditingHospital(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const defaultHospitals = hospitals.filter(h => h.type === 'default');
  const customHospitals = hospitals.filter(h => h.type === 'custom');

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Hospital Management</h1>
              <p className="text-gray-600 mt-1">Manage hospitals and add custom ones for your services</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Add Hospital
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="p-6">
          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingHospital ? 'Edit Hospital' : 'Add New Hospital'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Hospital Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter hospital name"
                  />
                  <p className="mt-1 text-sm text-gray-500">{formData.name.length}/100 characters</p>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description (Optional)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={250}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter hospital description (optional)"
                  />
                  <p className="mt-1 text-sm text-gray-500">{formData.description.length}/250 characters</p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={submitting || !formData.name.trim()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Saving...' : (editingHospital ? 'Update Hospital' : 'Add Hospital')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Default Hospitals */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Default Hospitals</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {defaultHospitals.map((hospital) => (
                <div key={hospital.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{hospital.name}</h3>
                      {hospital.description && (
                        <p className="text-sm text-gray-600 mt-1">{hospital.description}</p>
                      )}
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                        Default
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Hospitals */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Your Custom Hospitals ({customHospitals.length})
            </h2>
            {customHospitals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="mt-2">No custom hospitals yet</p>
                <p className="text-sm">Add your first custom hospital to get started</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {customHospitals.map((hospital) => (
                  <div key={hospital.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{hospital.name}</h3>
                        {hospital.description && (
                          <p className="text-sm text-gray-600 mt-1">{hospital.description}</p>
                        )}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                          Custom
                        </span>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => handleEdit(hospital)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit hospital"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(hospital)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete hospital"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HospitalManagement;